import AddIcon from '@mui/icons-material/Add'
import { AccordionDetails, Box, Button } from '@mui/material'

import React, { useEffect, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import StyledAccordion from '../../../components/Accordions/StyledAccordion'
import StyledAccordionSummary from '../../../components/Accordions/StyledAccordionSummary'
import { removeKeys } from '../../utils'
import { useOrderFormContext } from '../context/OrderFormContext'
import { useOrderStatus } from '../hooks/useOrderStatus'
import ConsignmentInfo from './ConsignmentInfo'
import PackagingItem from './PackagingItem'
import messages from './messages'

interface PackagingFormProps {
  consignmentIndex: number
  disabled?: boolean
  variant?: 'summary' | 'form'
}

const PackagingForm: React.FC<PackagingFormProps> = ({
  consignmentIndex,
  disabled = false,
  variant,
}) => {
  const { control, watch } = useFormContext()
  const { isNotDrafted } = useOrderStatus()
  const { append: appendLoad, remove } = useFieldArray({
    control,
    name: `bookings.${consignmentIndex}.packaging`,
  })

  const { formatMessage } = useIntl()

  const packaging = watch(`bookings.${consignmentIndex}.packaging`, [])

  const copyPackaging = (index: number) => {
    const packages = packaging[index]
    const load = removeKeys(packages, [
      'id',
      'loadDetailId',
      'physicalPropertiesId',
      'temperatureSettingId',
      'orderId',
      'bookingId',
      'consignmentId',
      'waypointId',
    ])
    appendLoad(load)
  }

  useEffect(() => {
    if (packaging.length === 0) {
      appendLoad({
        numberOfPackages: 1,
        temperatureControlled: false,
        useDeclaredValue: true,
        physicalProperties: {
          useDimensions: true,
          isRectangular: true,
          weightMeasureUnitId: null,
          lengthMeasureUnitId: null,
          volumeMeasureUnitId: null,
          densityMeasureUnitId: null,
          netWeight: 0,
          tareWeight: 0,
        },
      })
    }
  }, [packaging])

  const [expanded, setExpanded] = useState(true)
  const { currentPage } = useOrderFormContext()
  const effectiveVariant = currentPage === 5 ? 'summary' : 'form'


  const handleRemove = (index: number) => {
    if (packaging.length > 1) {
      remove(index)
    }
  }

  const handleToggleAccordion = () => {
    setExpanded((prev) => !prev)
  }

  const renderPackagingItem = () => {
    return (
      <>
        {effectiveVariant === 'form' && <ConsignmentInfo consignmentIndex={consignmentIndex} />}
        <StyledAccordion expanded={expanded} onChange={handleToggleAccordion}>
          <StyledAccordionSummary
            index={`packaging-content-${consignmentIndex}`}
            title={formatMessage(messages.loads)}
          />
          <AccordionDetails>
            {packaging.map((item: any, index: any) => (
              <Box sx={{ mb: '16px' }} key={item.id}>
                <PackagingItem
                  key={item.id}
                  consignmentIndex={consignmentIndex}
                  packagingIndex={index}
                  itemId={item.id}
                  remove={handleRemove}
                  copy={copyPackaging}
                  disabled={disabled}
                  totalPackagingItems={packaging.length}
                />
              </Box>
            ))}
            {currentPage == 2 && !disabled && !isNotDrafted && (
              <Button
                variant='outlined'
                startIcon={<AddIcon />}
                onClick={() =>
                  appendLoad({
                    numberOfPackages: 1,
                    temperatureControlled: false,
                    useDeclaredValue: true,
                    physicalProperties: {
                      useDimensions: true,
                      isRectangular: true,
                      weightMeasureUnitId: null,
                      lengthMeasureUnitId: null,
                      volumeMeasureUnitId: null,
                      densityMeasureUnitId: null,
                      netWeight: 0,
                      tareWeight: 0,
                    },
                  })
                }
                sx={{ marginBottom: '16px' }}
              >
                {formatMessage(messages.addLoad)}
              </Button>
            )}
          </AccordionDetails>
        </StyledAccordion>
      </>
    )
  }
  return <>{renderPackagingItem()}</>
}

export default PackagingForm
